import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCheck } from 'lucide-react';
import { useEventsStore } from '@/stores/events';
import { useFleetStore } from '@/stores/fleet';
import { translateEvent } from '@/lib/traccarLabels';
import { cn, formatDateTime, formatRelativeTime } from '@/lib/utils';

const PREVIEW_LIMIT = 10;

/**
 * Sino no header que abre um dropdown com as últimas notificações recebidas.
 * Mostra badge vermelho com contador de não-lidas. Click no item marca lido
 * e navega para o detalhe da máquina (se houver deviceId).
 */
export function HeaderNotifications() {
  const items = useEventsStore((s) => s.items);
  const markRead = useEventsStore((s) => s.markRead);
  const markAllRead = useEventsStore((s) => s.markAllRead);
  const devices = useFleetStore((s) => s.devices);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const unreadCount = items.filter((e) => !e.read).length;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const recent = items.slice(0, PREVIEW_LIMIT);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Notificações"
        className={cn(
          'relative inline-flex items-center justify-center rounded-full border border-border bg-card p-1.5',
          'transition-colors hover:bg-accent',
        )}
        aria-label={`Notificações${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
      >
        <Bell className="h-4 w-4 text-foreground/80" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-status-blocked px-1 text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute right-0 top-full z-50 mt-2 w-[calc(100vw-1rem)] max-w-sm overflow-hidden rounded-xl border border-border bg-card shadow-xl',
            'sm:w-96 sm:max-w-none',
          )}
        >
          <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div>
              <p className="text-sm font-bold">Notificações</p>
              <p className="text-[11px] text-muted-foreground">
                {unreadCount > 0
                  ? `${unreadCount} não lida${unreadCount === 1 ? '' : 's'}`
                  : 'Tudo em dia'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => markAllRead()}
              disabled={unreadCount === 0}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10 disabled:opacity-40"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Marcar todas
            </button>
          </header>

          {recent.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-6 text-center">
              <BellOff className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">
                Sem notificações. Crie regras no Trackmax para receber alertas aqui.
              </p>
            </div>
          ) : (
            <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto sm:max-h-80">
              {recent.map((e) => {
                const device = e.deviceId != null ? devices.get(e.deviceId) : undefined;
                return (
                  <li key={e.uid}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!e.read) markRead([e.uid]);
                        setOpen(false);
                        if (device) navigate(`/devices/${device.id}`);
                        else navigate('/notifications');
                      }}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50',
                        !e.read && 'bg-primary/5',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                          !e.read ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground',
                        )}
                      >
                        <Bell className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="truncate text-xs font-semibold">
                            {translateEvent(e.type)}
                            {!e.read && (
                              <span className="ml-1.5 inline-block h-1 w-1 rounded-full bg-primary align-middle" />
                            )}
                          </p>
                          <span
                            className="shrink-0 text-[10px] text-muted-foreground"
                            title={formatDateTime(e.serverTime)}
                          >
                            {formatRelativeTime(e.serverTime)}
                          </span>
                        </div>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {device ? device.name : e.deviceId ? `Máquina ${e.deviceId}` : 'Sistema'}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <footer className="border-t border-border bg-secondary/40 px-4 py-2 text-center">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="inline-block text-[11px] font-semibold text-primary hover:underline"
            >
              Ver todas
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
